import { useRef, useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  format,
  parseISO,
  startOfMonth,
  endOfMonth,
  eachDayOfInterval,
  isSameMonth,
  isSameDay,
  isToday,
  addMonths,
  subMonths,
  addDays,
  subDays,
  getDay,
} from 'date-fns'
import { es } from 'date-fns/locale'
import { ChevronLeft, ChevronRight, Plus, Loader2, CalendarDays, Car, CheckCircle2, Truck, Pencil, CloudDownload } from 'lucide-react'
import { usePickups } from '@/hooks/queries/usePickups'
import { useDeliveries } from '@/hooks/queries/useDeliveries'
import {
  useOperatorDailyLog,
  useOperatorDailyLogSummary,
  useUpsertOperatorDailyLog,
} from '@/hooks/queries/useOperatorDailyLog'
import { useAuth } from '@/context/AuthContext'
import { getCachedPickupIds } from '@/lib/db'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Input } from '@/components/ui/input'
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from '@/components/ui/card'
import { getStatusColor, getStatusDotColor, cn } from '@/lib/utils'
import { usePickupOptions } from '@/hooks/queries/usePickupOptions'
import type { Pickup, PickupStatus } from '@/types'

const WEEKDAYS_SHORT = ['L', 'M', 'X', 'J', 'V', 'S', 'D']
const WEEKDAYS_LONG  = ['Lun', 'Mar', 'Mié', 'Jue', 'Vie', 'Sáb', 'Dom']

const formatKm = (value: string | number) => parseFloat(String(value)).toString()

export function PickupsCalendarPage() {
  const navigate = useNavigate()
  const panelRef = useRef<HTMLDivElement>(null)
  const [currentDate, setCurrentDate] = useState(new Date())
  const [selectedDay, setSelectedDay] = useState<Date | null>(new Date())
  const [statusFilter, setStatusFilter] = useState<PickupStatus | ''>('')

  const { role } = useAuth()
  const { data: pickupOptions } = usePickupOptions()
  const statusOptions = [{ value: '' as const, label: 'Todos' }, ...(pickupOptions?.statuses ?? [])]
  const logDate = selectedDay ? format(selectedDay, 'yyyy-MM-dd') : format(new Date(), 'yyyy-MM-dd')
  const summaryMonth = format(currentDate, 'yyyy-MM')
  const { data: existingLog } = useOperatorDailyLog(role === 'operator' ? logDate : '')
  const { data: kmSummary, isLoading: isKmSummaryLoading } = useOperatorDailyLogSummary(logDate, summaryMonth)
  const upsertLog = useUpsertOperatorDailyLog()
  const [kmInput, setKmInput] = useState('')
  const [kmError, setKmError] = useState('')
  const [logSaved, setLogSaved] = useState(false)
  const [cachedPickupIds, setCachedPickupIds] = useState<Set<number>>(new Set())

  // Pre-cargar km cuando cambia el día o llega el registro existente
  useEffect(() => {
    setKmInput(existingLog ? String(existingLog.km) : '')
    setKmError('')
    setLogSaved(false)
  }, [logDate, existingLog])

  const monthStart = startOfMonth(currentDate)
  const monthEnd = endOfMonth(currentDate)
  const days = eachDayOfInterval({ start: monthStart, end: monthEnd })

  const prefixCount = (getDay(monthStart) + 6) % 7
  const prefixDays = prefixCount > 0
    ? eachDayOfInterval({ start: subDays(monthStart, prefixCount), end: subDays(monthStart, 1) })
    : []

  const suffixCount = (7 - ((prefixCount + days.length) % 7)) % 7
  const suffixDays = suffixCount > 0
    ? eachDayOfInterval({ start: addDays(monthEnd, 1), end: addDays(monthEnd, suffixCount) })
    : []

  const visibleStart = prefixDays.length > 0 ? prefixDays[0] : monthStart
  const visibleEnd = suffixDays.length > 0 ? suffixDays[suffixDays.length - 1] : monthEnd

  const { data: pickups = [], isLoading } = usePickups({
    date_from: format(visibleStart, 'yyyy-MM-dd'),
    date_to: format(visibleEnd, 'yyyy-MM-dd'),
    status: statusFilter || undefined,
  })

  useEffect(() => {
    getCachedPickupIds()
      .then((ids) => setCachedPickupIds(new Set(ids)))
      .catch((err) => {
        console.error('Error cargando retiros offline:', err)
      })
  }, [currentDate, statusFilter])

  const deliveryDateFilter = selectedDay
    ? format(selectedDay, 'yyyy-MM-dd')
    : format(new Date(), 'yyyy-MM-dd')
  const { data: dayDeliveries = [] } = useDeliveries({
    date_from: deliveryDateFilter,
    date_to: deliveryDateFilter,
  })

  const getPickupsForDay = (day: Date): Pickup[] =>
    pickups.filter((p) =>
      isSameDay(new Date(p.scheduled_date + 'T00:00:00'), day)
    )

  const selectedDayPickups = selectedDay ? getPickupsForDay(selectedDay) : []
  const isPickupCached = (pickupId: number) => cachedPickupIds.has(pickupId)
  const adminKmSummary = kmSummary?.kind === 'admin' ? kmSummary : null
  const operatorKmSummary = kmSummary?.kind === 'operator' ? kmSummary : null

  const handleDayClick = (day: Date, isSelected: boolean) => {
    setSelectedDay(isSelected ? null : day)
    if (!isSelected) {
      setTimeout(() => {
        panelRef.current?.scrollIntoView({ behavior: 'smooth', block: 'nearest' })
      }, 50)
    }
  }

  return (
    <div className="space-y-4 sm:space-y-6">
      {/* Encabezado */}
      <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
        <div>
          <h1 className="text-xl font-bold text-gray-900 sm:text-2xl">Agenda de retiros</h1>
          <p className="text-sm text-muted-foreground">
            Visualiza y gestiona los retiros programados
          </p>
        </div>
        {role === 'admin' && (
          <Button className="w-full sm:w-auto" onClick={() => navigate('/pickups/new')}>
            <Plus className="mr-2 h-4 w-4" />
            Programar retiro
          </Button>
        )}
      </div>

      {/* Filtro por estado */}
      <div className="flex flex-wrap gap-1.5 sm:gap-2">
        {statusOptions.map((opt) => (
          <button
            key={opt.value}
            onClick={() => setStatusFilter(opt.value as PickupStatus | '')}
            className={cn(
              'rounded-full px-2.5 py-1 text-xs font-medium transition-colors sm:px-3',
              statusFilter === opt.value
                ? 'bg-[#383f2d] text-[#dff24b]'
                : 'bg-[#e6e6e6] text-[#383f2d] hover:bg-[#b9d94a]/40'
            )}
          >
            {opt.label}
          </button>
        ))}
      </div>

      <div className="grid gap-4 sm:gap-6 lg:grid-cols-[1fr_300px]">
        {/* Calendario */}
        <Card>
          <CardHeader className="pb-2 sm:pb-3">
            <div className="flex items-center justify-between">
              <CardTitle className="text-base capitalize">
                {format(currentDate, 'MMMM yyyy', { locale: es })}
              </CardTitle>
              <div className="flex items-center gap-1">
                <Button
                  variant="outline"
                  size="icon"
                  className="h-8 w-8"
                  onClick={() => setCurrentDate(subMonths(currentDate, 1))}
                  aria-label="Mes anterior"
                >
                  <ChevronLeft className="h-4 w-4" />
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  className="h-8 px-3 text-xs"
                  onClick={() => setCurrentDate(new Date())}
                >
                  Hoy
                </Button>
                <Button
                  variant="outline"
                  size="icon"
                  className="h-8 w-8"
                  onClick={() => setCurrentDate(addMonths(currentDate, 1))}
                  aria-label="Mes siguiente"
                >
                  <ChevronRight className="h-4 w-4" />
                </Button>
              </div>
            </div>
          </CardHeader>
          <CardContent className="p-2 sm:p-3">
            {isLoading ? (
              <div className="flex items-center justify-center py-16">
                <Loader2 className="h-8 w-8 animate-spin text-[#7c8c43]" />
              </div>
            ) : (
              <>
                {/* Encabezados días de semana */}
                <div className="mb-1 grid grid-cols-7 gap-0.5 sm:gap-1">
                  {WEEKDAYS_SHORT.map((d) => (
                    <div
                      key={d}
                      className="py-1 text-center text-[11px] font-medium text-gray-400 sm:hidden"
                    >
                      {d}
                    </div>
                  ))}
                  {WEEKDAYS_LONG.map((d) => (
                    <div
                      key={d}
                      className="hidden py-1.5 text-center text-xs font-medium text-gray-400 sm:block"
                    >
                      {d}
                    </div>
                  ))}
                </div>

                {/* Grid de días */}
                <div className="grid grid-cols-7 gap-0.5 sm:gap-1">
                  {[...prefixDays, ...days, ...suffixDays].map((day) => {
                    const dayPickups = getPickupsForDay(day)
                    const isSelected = selectedDay ? isSameDay(day, selectedDay) : false
                    const isDayToday = isToday(day)
                    const inMonth = isSameMonth(day, currentDate)

                    return (
                      <button
                        key={day.toISOString()}
                        onClick={() => handleDayClick(day, isSelected)}
                        className={cn(
                          'relative flex flex-col rounded-md p-1 text-left transition-colors sm:p-1.5',
                          'min-h-[42px] sm:min-h-[64px] lg:min-h-[70px]',
                          inMonth
                            ? 'hover:bg-[#e6e6e6]/50'
                            : 'opacity-40 hover:bg-[#e6e6e6]/30',
                          isSelected && 'ring-2 ring-[#b9d94a] bg-[#dff24b]/20',
                          isDayToday && !isSelected && 'bg-[#383f2d]/5 ring-1 ring-[#383f2d]/20'
                        )}
                      >
                        <span
                          className={cn(
                            'flex h-5 w-5 items-center justify-center rounded-full text-[11px] font-medium sm:h-6 sm:w-6 sm:text-xs',
                            isDayToday
                              ? 'bg-[#383f2d] text-[#dff24b]'
                              : inMonth
                                ? 'text-[#383f2d]'
                                : 'text-gray-400'
                          )}
                        >
                          {format(day, 'd')}
                        </span>

                        {/* Móvil: solo puntos de color */}
                        {dayPickups.length > 0 && (
                          <div className="mt-0.5 flex flex-wrap gap-0.5 sm:hidden">
                            {dayPickups.slice(0, 4).map((pickup) => (
                              <span
                                key={pickup.id}
                                className={cn(
                                  'h-1.5 w-1.5 rounded-full',
                                  getStatusDotColor(pickup.status),
                                  isPickupCached(pickup.id) && 'ring-1 ring-white ring-offset-1'
                                )}
                                title={isPickupCached(pickup.id) ? 'Disponible sin conexión' : undefined}
                              />
                            ))}
                            {dayPickups.length > 4 && (
                              <span className="text-[8px] leading-none text-gray-400">
                                +{dayPickups.length - 4}
                              </span>
                            )}
                          </div>
                        )}

                        {/* sm+: etiquetas con texto */}
                        <div className="mt-1 hidden space-y-0.5 sm:block">
                          {dayPickups.slice(0, 3).map((pickup) => (
                            <div
                              key={pickup.id}
                              className={cn(
                                'flex items-center gap-1 rounded px-1 py-0.5 text-[10px] leading-tight',
                                getStatusColor(pickup.status)
                              )}
                            >
                              <span
                                className={cn(
                                  'h-1.5 w-1.5 flex-shrink-0 rounded-full',
                                  getStatusDotColor(pickup.status)
                                )}
                              />
                              <span className="truncate">
                                {pickup.client_branch?.name
                                  ? `${pickup.client?.trade_name ?? `#${pickup.id}`} · ${pickup.client_branch.name}`
                                  : pickup.client?.trade_name ?? `#${pickup.id}`}
                              </span>
                              {isPickupCached(pickup.id) && (
                                <CloudDownload
                                  className="h-3 w-3 flex-shrink-0"
                                  aria-label="Disponible sin conexión"
                                />
                              )}
                            </div>
                          ))}
                          {dayPickups.length > 3 && (
                            <p className="px-1 text-[10px] text-gray-400">
                              +{dayPickups.length - 3} más
                            </p>
                          )}
                        </div>
                      </button>
                    )
                  })}
                </div>
              </>
            )}
          </CardContent>
        </Card>

        {/* Panel lateral: retiros del día seleccionado */}
        <div ref={panelRef} className="space-y-4">
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-sm capitalize">
                {selectedDay
                  ? format(selectedDay, "EEEE d 'de' MMMM", { locale: es })
                  : 'Selecciona un día'}
              </CardTitle>
            </CardHeader>
            <CardContent>
              {!selectedDay ? (
                <div className="flex flex-col items-center py-8 text-center">
                  <CalendarDays className="mb-2 h-8 w-8 text-gray-300" />
                  <p className="text-sm text-gray-400">
                    Toca un día del calendario para ver sus retiros
                  </p>
                </div>
              ) : selectedDayPickups.length === 0 ? (
                <div className="flex flex-col items-center py-8 text-center">
                  <p className="text-sm text-gray-400">
                    No hay retiros programados para este día
                  </p>
                  {role === 'admin' && (
                    <Button
                      size="sm"
                      variant="outline"
                      className="mt-3"
                      onClick={() => navigate(`/pickups/new?date=${format(selectedDay, 'yyyy-MM-dd')}`)}
                    >
                      <Plus className="mr-1 h-3 w-3" />
                      Programar retiro
                    </Button>
                  )}
                </div>
              ) : (
                <div className="space-y-3">
                  <ul className="space-y-3">
                    {selectedDayPickups.map((pickup) => (
                      <li
                        key={pickup.id}
                        className="cursor-pointer rounded-lg border p-3 transition-colors hover:border-[#b9d94a]"
                        onClick={() => navigate(`/pickups/${pickup.id}`)}
                      >
                        <div className="flex items-start justify-between gap-2">
                          <div className="min-w-0 flex-1">
                            <p className="truncate text-sm font-medium text-gray-900">
                              {pickup.client?.trade_name ?? `Retiro #${pickup.id}`}
                              {isPickupCached(pickup.id) && (
                                <CloudDownload
                                  className="ml-1 inline h-3.5 w-3.5 text-[#7c8c43]"
                                  aria-label="Disponible sin conexión"
                                />
                              )}
                            </p>
                            {pickup.client_branch?.name && (
                              <p className="mt-0.5 truncate text-xs text-gray-600">
                                {pickup.client_branch.name}
                              </p>
                            )}
                            {(pickup.client_branch?.address ?? pickup.client?.address) && (
                              <p className="mt-0.5 truncate text-xs text-gray-500">
                                {pickup.client_branch?.address ?? pickup.client?.address}
                              </p>
                            )}
                            {pickup.notes && (
                              <p className="mt-0.5 truncate text-xs text-gray-500">
                                {pickup.notes}
                              </p>
                            )}
                          </div>
                          <Badge
                            className={cn('shrink-0 text-[10px]', getStatusColor(pickup.status))}
                          >
                            {pickup.status_label}
                          </Badge>
                        </div>
                        {pickup.client?.waste_subcategories && pickup.client.waste_subcategories.length > 0 && (
                          <div className="mt-2 flex flex-wrap gap-1">
                            {pickup.client.waste_subcategories.map((ws) => (
                              <span
                                key={ws.id}
                                className="rounded-full bg-[#dff24b]/30 px-2 py-0.5 text-[10px] font-medium text-[#383f2d] ring-1 ring-[#b9d94a]/50"
                              >
                                {ws.name}
                              </span>
                            ))}
                          </div>
                        )}
                        {pickup.is_recurring && (
                          <p className="mt-1 text-[10px] font-medium text-[#7c8c43]">
                            Recurrente ({pickup.recurrence_pattern_label})
                          </p>
                        )}
                      </li>
                    ))}
                  </ul>
                  {role === 'admin' && (
                    <Button
                      size="sm"
                      variant="outline"
                      className="w-full"
                      onClick={() => navigate(`/pickups/new?date=${format(selectedDay, 'yyyy-MM-dd')}`)}
                    >
                      <Plus className="mr-1 h-3 w-3" />
                      Agregar otro retiro
                    </Button>
                  )}
                </div>
              )}
            </CardContent>
          </Card>

          {/* Disposición Final */}
          {(role === 'admin' || role === 'operator') && (
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="flex items-center gap-2 text-sm">
                  <Truck className="h-4 w-4 text-[#7c8c43]" />
                  Disposición final
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                {dayDeliveries.length > 0 && (
                  <ul className="space-y-2">
                    {dayDeliveries.map((delivery) => (
                      <li
                        key={delivery.id}
                        className="flex items-start justify-between gap-2 rounded-lg border p-2 text-xs"
                      >
                        <div className="min-w-0 flex-1">
                          <p className="font-medium truncate">
                            {delivery.destination?.name ?? `Entrega #${delivery.id}`}
                          </p>
                          <p className="text-muted-foreground">
                            {delivery.items.length} ítem{delivery.items.length !== 1 ? 's' : ''}
                          </p>
                        </div>
                        <button
                          onClick={() => navigate(`/deliveries/${delivery.id}/edit`)}
                          className="shrink-0 rounded p-1 text-muted-foreground hover:bg-[#dff24b]/30 hover:text-[#383f2d] transition-colors"
                          aria-label="Editar entrega"
                        >
                          <Pencil className="h-3.5 w-3.5" />
                        </button>
                      </li>
                    ))}
                  </ul>
                )}
                <Button
                  size="sm"
                  variant="outline"
                  className="w-full"
                  onClick={() =>
                    navigate(`/deliveries/new?date=${deliveryDateFilter}`)
                  }
                >
                  <Plus className="mr-1 h-3 w-3" />
                  Registrar entrega
                </Button>
              </CardContent>
            </Card>
          )}

          {/* Kilometraje */}
          {role === 'operator' && (
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="flex items-center gap-2 text-sm">
                  <Car className="h-4 w-4 text-[#7c8c43]" />
                  Kilometraje del día
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <p className="text-xs text-muted-foreground capitalize">
                  {format(new Date(logDate + 'T00:00:00'), "EEEE d 'de' MMMM", { locale: es })}
                </p>
                <div className="flex gap-2">
                  <div className="flex-1">
                    <Input
                      type="number"
                      step="0.01"
                      min="0.01"
                      placeholder="km recorridos"
                      value={kmInput}
                      onChange={(e) => {
                        setKmInput(e.target.value)
                        setKmError('')
                        setLogSaved(false)
                      }}
                    />
                    {kmError && (
                      <p className="mt-1 text-xs text-red-600">{kmError}</p>
                    )}
                  </div>
                  <Button
                    size="sm"
                    disabled={upsertLog.isPending}
                    onClick={async () => {
                      const km = parseFloat(kmInput)
                      if (!kmInput || isNaN(km) || km <= 0) {
                        setKmError('Ingresa un valor mayor a 0')
                        return
                      }
                      await upsertLog.mutateAsync({ date: logDate, km })
                      setLogSaved(true)
                    }}
                  >
                    {upsertLog.isPending ? (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    ) : existingLog ? (
                      'Actualizar'
                    ) : (
                      'Guardar'
                    )}
                  </Button>
                </div>
                {logSaved && (
                  <div className="flex items-center gap-1.5 text-xs text-green-700">
                    <CheckCircle2 className="h-3.5 w-3.5" />
                    Kilometraje guardado correctamente
                  </div>
                )}
                <div className="rounded-lg bg-[#f5f6f1] px-3 py-2">
                  <p className="text-xs text-muted-foreground capitalize">
                    Total mensual · {format(currentDate, 'MMMM yyyy', { locale: es })}
                  </p>
                  <p className="text-lg font-semibold text-[#383f2d]">
                    {isKmSummaryLoading ? '...' : `${formatKm(operatorKmSummary?.monthly_km ?? '0')} km`}
                  </p>
                </div>
              </CardContent>
            </Card>
          )}

          {role === 'admin' && (
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="flex items-center gap-2 text-sm">
                  <Car className="h-4 w-4 text-[#7c8c43]" />
                  Kilometraje por operador
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <div className="space-y-0.5 text-xs text-muted-foreground">
                  <p className="capitalize">
                    Día: {format(parseISO(logDate), "EEEE d 'de' MMMM", { locale: es })}
                  </p>
                  <p className="capitalize">
                    Mes: {format(currentDate, 'MMMM yyyy', { locale: es })}
                  </p>
                </div>
                {isKmSummaryLoading ? (
                  <div className="flex items-center gap-2 text-sm text-muted-foreground">
                    <Loader2 className="h-4 w-4 animate-spin" />
                    Cargando kilometraje
                  </div>
                ) : !adminKmSummary || adminKmSummary.operators.length === 0 ? (
                  <p className="text-sm text-muted-foreground">
                    No hay operadores registrados.
                  </p>
                ) : (
                  <div className="overflow-hidden rounded-lg border">
                    <table className="w-full text-left text-xs">
                      <thead className="bg-[#f5f6f1] text-muted-foreground">
                        <tr>
                          <th className="px-2 py-2 font-medium">Operador</th>
                          <th className="px-2 py-2 text-right font-medium">Día</th>
                          <th className="px-2 py-2 text-right font-medium">Mes</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y">
                        {adminKmSummary.operators.map((operator) => (
                          <tr key={operator.operator_id}>
                            <td className="px-2 py-2 font-medium text-gray-900">
                              {operator.operator_name}
                            </td>
                            <td className="px-2 py-2 text-right tabular-nums">
                              {formatKm(operator.daily_km)} km
                            </td>
                            <td className="px-2 py-2 text-right tabular-nums">
                              {formatKm(operator.monthly_km)} km
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </CardContent>
            </Card>
          )}

          {/* Leyenda de estados */}
          <Card className="hidden sm:block">
            <CardHeader className="pb-2">
              <CardTitle className="text-xs font-medium uppercase tracking-wider text-gray-500">
                Leyenda
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-1.5">
              {(pickupOptions?.statuses ?? []).map((s) => (
                <div
                  key={s.value}
                  className={cn(
                    'flex items-center gap-1 rounded px-2 py-1 text-xs',
                    getStatusColor(s.value)
                  )}
                >
                  <span
                    className={cn('h-1.5 w-1.5 flex-shrink-0 rounded-full', getStatusDotColor(s.value))}
                  />
                  {s.label}
                </div>
              ))}
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  )
}
